import React, { createContext, useState, ReactNode, useEffect, useContext, useMemo, useCallback } from 'react';

interface UseTabNavigationProps {
  tabIndexForChildren: number;
  hasAreaFocus: boolean;
  isDuringNavigating: boolean;
  isDuringAction: boolean;
  startNavigation: () => void;
  endNavigation: (forceEnd?: boolean) => void;
  startAction: () => void;
  TabNavigationArea: React.FC;
  activeRowIndex: number | null;
  setActiveRowIndex: React.Dispatch<React.SetStateAction<number | null>>;
  forceStopAction: () => void;
}

interface TabNavigationContextProps {
  tabIndexForChildren: number;
  hasAreaFocus: boolean;
  isDuringNavigating: boolean;
  isDuringAction: boolean;
  activeRowIndex: number | null;
  setActiveRowIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  forceStopAction: () => void;
}
const TabNavigationContext = createContext<TabNavigationContextProps | null>(null);

export const hasRefFocus = (ref: HTMLElement | null) => !!ref && !!ref.matches(':focus');
export const hasRefFocusWithin = (ref: HTMLElement | null) => !!ref && !!ref.matches(':focus-within');

export const useTabNavigation = (
  areaRef: HTMLElement | null,
  onStartNavigation?: () => void,
  onEndNavigation?: (forceEnd?: boolean) => void,
  onStartAction?: () => void,
): UseTabNavigationProps => {
  const [isDuringNavigating, setIsDuringNavigating] = useState<boolean>(false);
  const [hasAreaFocus, setHasAreaFocus] = useState<boolean>(false);
  const [isDuringAction, setIsDuringAction] = useState<boolean>(false);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  const tabIndexForChildren = isDuringNavigating ? 0 : -1;

  const startNavigation = () => {
    setIsDuringNavigating(true);
    onStartNavigation && onStartNavigation();
  };

  const endNavigation = useCallback(
    (forceEnd?: boolean) => {
      setIsDuringNavigating((prevState) => {
        if (prevState) {
          onEndNavigation && onEndNavigation(forceEnd);
          return false;
        } else {
          return prevState;
        }
      });
      setIsDuringAction(false);
    },
    [onEndNavigation],
  );

  const startAction = useCallback(() => {
    setIsDuringAction(true);
    onStartAction && onStartAction();
  }, [onStartAction]);

  const forceStopAction = useCallback(() => {
    setIsDuringAction(false);
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    const hasFocusWithin = hasRefFocusWithin(areaRef);
    const hasFocus = hasRefFocus(areaRef);
    if (!hasFocusWithin) {
      setHasAreaFocus(false);
      setIsDuringNavigating(false);
    }

    switch (e.key) {
      case 'Escape':
        endNavigation();
        break;
      case 'Enter':
        if (hasFocus) {
          startNavigation();
        }
        break;
    }
  };

  const handleFocus = () => {
    setHasAreaFocus(true);
  };

  useEffect(() => {
    areaRef?.addEventListener('keydown', handleKeyDown);
    areaRef?.addEventListener('focus', handleFocus);
    return () => {
      areaRef?.removeEventListener('keydown', handleKeyDown);
      areaRef?.removeEventListener('focus', handleFocus);
    };
  }, [handleKeyDown]);

  const TabNavigationArea = useMemo(() => {
    const Component: React.FC = (props: { children?: ReactNode }) => {
      return (
        <TabNavigationContext.Provider
          value={{
            tabIndexForChildren,
            hasAreaFocus,
            isDuringNavigating,
            isDuringAction,
            activeRowIndex,
            setActiveRowIndex,
            forceStopAction,
          }}
        >
          {props.children}
        </TabNavigationContext.Provider>
      );
    };

    return Component;
  }, [tabIndexForChildren, hasAreaFocus, isDuringNavigating, isDuringAction, activeRowIndex, forceStopAction]);

  return {
    tabIndexForChildren,
    hasAreaFocus,
    isDuringNavigating,
    startNavigation,
    endNavigation,
    startAction,
    isDuringAction,
    TabNavigationArea,
    activeRowIndex,
    setActiveRowIndex,
    forceStopAction,
  };
};

export const useTabNavigationDetails = (): TabNavigationContextProps => {
  const contextData = useContext(TabNavigationContext);
  if (!contextData) {
    return {
      tabIndexForChildren: -1,
      hasAreaFocus: false,
      isDuringNavigating: false,
      isDuringAction: false,
      activeRowIndex: null,
      forceStopAction: () => {
        console.log('Missing forceStopAction');
      },
    };
  }

  return contextData;
};
